/**
 * Email templates — the list.
 *
 * Every transactional message the platform sends, grouped by purpose (account lifecycle,
 * money receipts, support and sign-in, and the one that goes to the operator rather than to a
 * customer). Each row opens the two-mode editor. Templates default to the Business-Settings
 * logo and colours, so a rebrand flows through without touching a single template. Superadmin
 * only.
 */
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import type { EmailTemplate } from '../contracts/types.js';
import { Badge, Card, ErrorBox, LoadingState } from '../components/ui.js';
import { IconExternalLink, IconMail, IconPalette, IconSend } from '../components/icons.js';
import { useAsync } from '../hooks/useAsync.js';
import { relativeTime } from '../lib/format.js';

/**
 * How the templates are grouped, and what happens to one this list has never heard of.
 *
 * ## A key missing from here used to mean a template nobody could edit
 *
 * The rows are drawn by walking these groups and looking each key up in the response, so a
 * template the server serves and this list does not name was simply not rendered — no row, no
 * link, no way in. Three had accumulated that way: the new-device sign-in code, the sign-in
 * alert, and the operator's own platform alert. All three were live, all three were being sent
 * to real inboxes, and none of them could be read or edited from this console.
 *
 * So there is a catch-all now. `ungroupedOf` puts anything not named above under "Everything
 * else", which means the worst a future key can do is land in a plainly labelled group rather
 * than disappear. `FeesPage` reached the same conclusion for the same reason: a page that
 * renders only the keys it recognises hides a live one, and a hidden one is the one nobody can
 * answer a question about.
 */
const GROUPS: ReadonlyArray<{ title: string; hint: string; keys: readonly EmailTemplate['key'][] }> = [
  {
    title: 'Account lifecycle',
    hint: 'Sign-up, verification and password flows',
    keys: ['welcome', 'email_otp', 'password_reset'],
  },
  {
    title: 'Money receipts',
    hint: 'One receipt per kind of money movement',
    keys: [
      'receipt_sell',
      'receipt_convert',
      'receipt_withdraw',
      'receipt_transfer',
      'receipt_funding',
      'receipt_giftcard',
    ],
  },
  {
    title: 'Support & security',
    hint: 'Conversations, sign-in notices and account-safety alerts',
    keys: [
      'support_reply',
      'missed_chat',
      'security_alert',
      // The code that lets a phone this account has never used finish signing in. Its own
      // message rather than the sign-up code, because a customer who is sent "your
      // verification code" while signing in cannot tell whether it is the right one.
      'device_verification',
      // Sent every time somebody signs in. Deliberately not folded into the alert above: a
      // sign-in is the ordinary case, and putting it under the alarm trains people to ignore
      // the alarm.
      'login_alert',
    ],
  },
  {
    title: 'Messages to you',
    hint: 'Sent to your own address, never to a customer',
    keys: ['admin_platform_event'],
  },
];

/** Every template the server sent that no group above claims. Never dropped, only grouped. */
function ungroupedOf(templates: readonly EmailTemplate[]): EmailTemplate[] {
  const claimed = new Set<string>(GROUPS.flatMap((group) => [...group.keys]));
  return templates.filter((t) => !claimed.has(t.key));
}

export function EmailTemplatesPage(): JSX.Element {
  const { api } = useAuth();
  const state = useAsync(() => api.listEmailTemplates(), []);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Email templates</h1>
        <p className="page-head__sub">
          Every message the platform sends by email, almost all of them to customers and one to you. Edit it visually or in HTML, preview it, and send yourself a test. The header and footer come from your layout, so changing that reskins all of them.
        </p>
      </div>

      <Card title="Everything else email">
        <div className="quicklinks">
          <Link to="/email-settings" className="quicklink">
            <span className="quicklink__icon">
              <IconPalette size={18} />
            </span>
            <span className="quicklink__text">
              <span className="quicklink__label">Email settings</span>
              <span className="quicklink__hint">Mail server, branding, footer and the image library</span>
            </span>
            <IconExternalLink size={15} />
          </Link>
          <Link to="/email-broadcast" className="quicklink">
            <span className="quicklink__icon">
              <IconSend size={18} />
            </span>
            <span className="quicklink__text">
              <span className="quicklink__label">Email broadcast</span>
              <span className="quicklink__hint">Write to one customer, several, or a whole group</span>
            </span>
            <IconExternalLink size={15} />
          </Link>
        </div>
      </Card>

      {state.loading ? (
        <LoadingState label="Loading templates…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : state.data === null ? (
        <ErrorBox message="Templates are not available." />
      ) : (
        <div className="tpl-groups">
          {GROUPS.map((group) => (
            <TemplateGroup
              key={group.title}
              title={group.title}
              hint={group.hint}
              rows={group.keys
                .map((k) => state.data!.templates.find((t) => t.key === k))
                .filter((t): t is EmailTemplate => t !== undefined)}
            />
          ))}
          <TemplateGroup
            title="Everything else"
            hint="Messages this console has no group for yet. They are live and editable like any other."
            rows={ungroupedOf(state.data.templates)}
          />
        </div>
      )}
    </>
  );
}

/** One card of template rows. Renders nothing at all when the group is empty. */
function TemplateGroup({
  title,
  hint,
  rows,
}: {
  title: string;
  hint: string;
  rows: readonly EmailTemplate[];
}): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <Card title={title} flush>
      <div className="tpl-group__hint">{hint}</div>
      <ul className="tpl-list">
        {rows.map((t) => (
          <li key={t.key}>
            <Link to={`/email-templates/${encodeURIComponent(t.key)}`} className="tpl-row">
              <span className="tpl-row__icon">
                <IconMail size={17} />
              </span>
              <span className="tpl-row__main">
                <span className="tpl-row__name">{t.name}</span>
                <span className="tpl-row__subject">{t.subject}</span>
                {/* The preheader is the second-most-read text in an email and the least often
                    written, so the list shows when one is missing rather than making somebody
                    open every template to find out. */}
                <span className="tpl-row__pre faint">
                  {t.preheader.trim() === '' ? 'No preheader set' : t.preheader}
                </span>
              </span>
              <span className="tpl-row__meta">
                {t.enabled ? (
                  <Badge tone="green" dot>
                    On
                  </Badge>
                ) : (
                  <Badge tone="neutral" dot>
                    Off
                  </Badge>
                )}
                <span className="faint tpl-row__when">Edited {relativeTime(t.updatedAt)}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
